"use client";

import React, { useEffect, useRef, useState } from "react";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
interface StatCounterProps {
  end: number;
  suffix?: string;
  isVisible: boolean;
}
function StatCounter({
  end,
  suffix = "",
  isVisible
}: StatCounterProps) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!isVisible) return;
    let startTimestamp: number | null = null;
    const duration = 2500;
    const step = (timestamp: number) => {
      if (!startTimestamp) startTimestamp = timestamp;
      const progress = Math.min((timestamp - startTimestamp) / duration, 1);
      const easeProgress = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
      setCount(Math.floor(easeProgress * end));
      if (progress < 1) {
        window.requestAnimationFrame(step);
      } else {
        setCount(end);
      }
    };
    window.requestAnimationFrame(step);
  }, [end, isVisible]);
  return <span>
      {count.toLocaleString()}
      {suffix}
    </span>;
}
const metricsData = [{
  label: "Avg. Response Time",
  value: 4.2,
  suffix: "m"
}, {
  label: "Officers on Duty",
  value: 142,
  suffix: ""
}, {
  label: "Community Hours",
  value: 12000,
  suffix: "+"
}, {
  label: "Crime Reduction",
  value: 18,
  suffix: "%"
}];
export default function DepartmentalTransparency() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="224325" id="departmental-transparency" className="bg-muted text-foreground relative overflow-hidden py-24 lg:py-32">
      {}
      <div className="absolute inset-0 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1689786/img/metropolitan_grid_pattern.png)] bg-cover bg-center opacity-5 mix-blend-multiply pointer-events-none" aria-hidden="true" />

      <div className="container mx-auto px-4 relative z-10" ref={ref}>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          {metricsData.map((metric, i) => <div key={metric.label} data-index={i} className="flex flex-col items-center justify-center text-center">
              {}
              <div className="relative mb-6">
                <div className="absolute inset-0 rounded-md bg-secondary opacity-20 blur-[4px] animate-pulse" />
                <div className="relative size-2.5 rounded-md bg-secondary shadow-sm" />
              </div>

              {}
              <div className="text-5xl md:text-6xl lg:text-7xl font-bold font-default text-primary tracking-tighter mb-3">
                <StatCounter end={metric.value} suffix={metric.suffix} isVisible={isVisible} />
              </div>

              {}
              <div className="text-sm md:text-base font-medium font-mono text-muted-foreground uppercase tracking-[0.2em]">
                {metric.label}
              </div>
            </div>)}
        </div>
      </div>
    </section>;
}